import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { Link } from "~/components/RouterLink";
import styles from "../live-seo-page.module.css";

type ContactLocalization = {
	title: string;
	heading: string;
	intro: string;
	secondLead: string;
	bracesText: string;
	middleText: string;
	invisalignText: string;
	closingText: string;
	hoursHeading: string;
	hours: string[];
	locationHeading: string;
};

const contactLocalization = {
	[Locale.EN]: {
		title: "Contact Us",
		heading: "Contact Miami Orthodontic Specialists",
		intro:
			"The team at Miami Orthodontic Specialists believes that everyone deserves the self-confidence that comes with having a beautiful and healthy smile.",
		secondLead: "Whether you're interested in traditional ",
		bracesText: "braces",
		middleText: " or exploring your options for ",
		invisalignText: "Invisalign in Miami",
		closingText:
			", our team is here to help. We are dedicated to forming lasting relationships with our patients and design personalized treatment plans that exceed expectations. Please feel free to contact us today - we look forward to serving you!",
		hoursHeading: "Office Hours",
		hours: [
			"Monday : 8:30am - 5:00pm",
			"Tuesday : 8:30am - 5:00pm",
			"Wednesday : 8:30am - 5:00pm",
			"Thursday : 8:30am - 5:00pm",
			"Friday : 8:30am - 5:00pm",
			"Saturday : Closed",
			"Sunday : Closed",
		],
		locationHeading: "Our Location",
	},
	[Locale.ES]: {
		title: "Contáctenos",
		heading: "Comunícate con Miami Orthodontic Specialists",
		intro:
			"El equipo de Miami Orthodontic Specialists cree que todos merecen la seguridad que viene con una sonrisa hermosa y saludable.",
		secondLead: "Ya sea que te interesen los ",
		bracesText: "brackets tradicionales",
		middleText: " o estés explorando tus opciones de ",
		invisalignText: "Invisalign en Miami",
		closingText:
			", nuestro equipo está aquí para ayudarte. Nos dedicamos a crear relaciones duraderas con nuestros pacientes y a diseñar planes de tratamiento personalizados que superen las expectativas. Comunícate con nosotros hoy; esperamos poder atenderte.",
		hoursHeading: "Horario de atención",
		hours: [
			"Lunes : 8:30am - 5:00pm",
			"Martes : 8:30am - 5:00pm",
			"Miércoles : 8:30am - 5:00pm",
			"Jueves : 8:30am - 5:00pm",
			"Viernes : 8:30am - 5:00pm",
			"Sábado : Cerrado",
			"Domingo : Cerrado",
		],
		locationHeading: "Nuestra ubicación",
	},
} satisfies Record<Locale, ContactLocalization>;

export function ContactPage() {
	const getLocalization = useLocalization(contactLocalization);

	return (
		<Flex direction="column">
			<HeroStyle2
				heroImageUrl="/miami-ortho-5.jpg"
				title={getLocalization("title")}
				backgroundPosition="center"
			/>
			<Box className={styles.contactTopBand}>
				<Box className={styles.contactTopInner}>
					<Box className={styles.contactTopGrid}>
						<Box className={styles.contactCopy}>
							<Heading as="h3" className={styles.contactHeading}>
								{getLocalization("heading")}
							</Heading>
							<Text as="p" className={styles.contactText}>
								{getLocalization("intro")}
							</Text>
							<Text as="p" className={styles.contactText}>
								{getLocalization("secondLead")}
								<Link className={styles.inlineTextLink} to="/miami-braces/">
									{getLocalization("bracesText")}
								</Link>
								{getLocalization("middleText")}
								<Link className={styles.inlineTextLink} to="/miami-invisalign/">
									{getLocalization("invisalignText")}
								</Link>
								{getLocalization("closingText")}
							</Text>
						</Box>
						<Box className={styles.contactHoursBlock}>
							<Heading as="h3" className={styles.contactHoursTitle}>
								{getLocalization("hoursHeading")}
							</Heading>
							<Text
								as="p"
								className={`${styles.contactText} ${styles.contactHoursList}`}
							>
								{getLocalization("hours").map((hour) => {
									const [day, time] = hour.split(" : ");

									return (
										<span className={styles.contactLine} key={hour}>
											<strong>{day}</strong>: {time}
										</span>
									);
								})}
							</Text>
						</Box>
					</Box>
				</Box>
			</Box>
			<Box className={styles.contactBand}>
				<Box className={styles.contactInner}>
					<Box className={styles.contactMapLocationGrid}>
						<Box className={styles.contactLocation}>
							<Text as="p" className={styles.contactText}>
								<strong>{getLocalization("locationHeading")}</strong>
								<br />
								8525 SW 92nd Street
								<br />
								Suite B-9
								<br />
								Miami, FL 33156
							</Text>
							<Text as="p" className={styles.contactText}>
								<strong>Phone</strong>
								<br />
								<Link className={styles.plainTextLink} to="tel:305-407-9000">
									(305) 407-9000
								</Link>
							</Text>
						</Box>
						<Box className={styles.contactMapWrap}>
							<iframe
								title="Miami Orthodontic Specialists office map"
								src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3595.594000117996!2d-80.33588008454495!3d25.684750018048977!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d9c7107399af23%3A0xaf183ae55f79561b!2sMiami%20Orthodontic%20Specialists%2C%20Dr.%20Carmen%20Garcia-Paul!5e0!3m2!1sen!2sus!4v1679341047815!5m2!1sen!2sus"
								className={styles.contactMap}
								loading="lazy"
								referrerPolicy="no-referrer-when-downgrade"
							/>
						</Box>
					</Box>
				</Box>
			</Box>
			<Box className={styles.formWrap}>
				<HomeConsultForm source="contact-us" />
			</Box>
		</Flex>
	);
}
